import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { takeReturnTo } from "../lib/deepLink";
import { Spinner, PrimaryButton } from "../components/ui";

/**
 * Landing point for SailPoint's redirect back from its hosted login page.
 * Trades the one-time authorization code for a session, then drops the code
 * and state out of the URL so a refresh can't try to replay them.
 */
export default function AuthCallbackPage() {
  const { finishLogin } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [handingOff, setHandingOff] = useState(false);
  // React 18 StrictMode mounts effects twice in development; the code is
  // single-use, so a second exchange would fail against an already-consumed
  // state. Guard so it only ever runs once.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const denied = params.get("error_description") || params.get("error");

    if (denied) {
      setError(denied);
      return;
    }
    if (!code || !state) {
      setError("That sign-in link was incomplete. Please try again.");
      return;
    }

    finishLogin(code, state)
      .then((result) => {
        if (result?.native) {
          // Started by the iOS app. iOS sandboxes the in-app browser this page
          // runs in, so it cannot open our custom URL scheme — with or without
          // a tap. The app polls for the session instead, leaving nothing to do
          // here but confirm and let the user go back.
          setHandingOff(true);
          return;
        }
        // Navigate through the router, not history.replaceState — the latter
        // changes the URL without telling React Router, so this screen would
        // keep rendering (spinning forever) even though the session is live.
        // "replace" also drops the spent code out of history.
        // A deep link's page (remembered when sign-in started), else Home.
        navigate(takeReturnTo() || "/", { replace: true });
      })
      .catch((err) => setError(err.message));
  }, [finishLogin, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6">
      <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-5 shadow-sm">
        <Shield size={32} className="text-white" />
      </div>
      {error ? (
        <div className="w-full max-w-sm text-center">
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 mb-4">
            {error}
          </p>
          <PrimaryButton onClick={() => navigate("/", { replace: true })}>
            Back to sign in
          </PrimaryButton>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 text-gray-500 text-sm w-full max-w-xs">
          {handingOff ? (
            <>
              <p className="text-center text-gray-700 font-medium">You're signed in.</p>
              <p className="text-xs text-gray-400 text-center">
                Close this page to return to Admin Studio — it's already
                picking up your session.
              </p>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Spinner size={18} />
              Completing sign-in…
            </div>
          )}
        </div>
      )}
    </div>
  );
}
